import { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { saveBlobResponse } from '../utils/download';
import FeatureNotice from './FeatureNotice';
import { formatDate } from '../utils/dateFormat';

const lastMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const n2 = (v) => (v === undefined || v === null || v === '' ? '-' : Number(v).toFixed(2));

const blobError = async (error, fallback) => {
  let data = error.response?.data;
  if (data instanceof Blob) data = await data.text();
  return typeof data === 'string' && data ? data : fallback;
};

const STATUS_LABEL = {
  MATCHED: 'Matched', MISMATCH: 'Amounts differ', PROBABLE_MATCH: 'Probable match',
  MISSING_IN_BOOKS: 'Not in your books', NOT_IN_2B: 'Not in 2B'
};
const STATUS_COLOR = { MATCHED: 'success', MISMATCH: 'danger', PROBABLE_MATCH: 'warning', MISSING_IN_BOOKS: 'danger', NOT_IN_2B: 'warning' };

/** Compares a GSTR-2B JSON downloaded from the GST portal with the vendor invoices and return notes in the books. */
function Gstr2bReconciliation() {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('ALL');

  const reconcile = async () => {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    try {
      setBusy(true);
      const response = await axios.post('/api/gst-returns/gstr2b/reconcile', body);
      setResult(response.data);
      setFilter('ALL');
    } catch (error) {
      setResult(null);
      toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Could not read the file');
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = () => {
    const header = ['Section', 'Status', 'Supplier GSTIN', 'Supplier', 'Number', 'Date', '2B taxable', '2B IGST', '2B CGST', '2B SGST',
      'Books ref', 'Books taxable', 'Books IGST', 'Books CGST', 'Books SGST', 'Note'];
    const esc = (v) => {
      let t = v === null || v === undefined ? '' : String(v);
      if (/^[=+\-@]/.test(t)) t = `'${t}`;
      return `"${t.replace(/"/g, '""')}"`;
    };
    const lines = result.rows.map((r) => [r.section, STATUS_LABEL[r.status], r.supplierGstin, r.supplierName, r.number, r.date,
      r.taxable2b, r.igst2b, r.cgst2b, r.sgst2b, r.booksRef, r.booksTaxable, r.booksIgst, r.booksCgst, r.booksSgst, r.message].map(esc).join(','));
    const blob = new Blob([`\uFEFF${[header.map(esc).join(','), ...lines].join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gstr2b-reconciliation-${result.period || 'file'}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const rows = result ? result.rows.filter((r) => filter === 'ALL' || r.status === filter) : [];
  const sm = result?.summary;

  return (
    <Card className="mb-4">
      <Card.Header><h5 className="mb-0">GSTR-2B reconciliation</h5></Card.Header>
      <Card.Body>
        <p className="text-muted">
          Download your GSTR-2B as JSON from the GST portal (Returns &gt; GSTR-2B &gt; Download) and upload it here. It is compared with the
          vendor invoices and return notes you have recorded, so credit you can&apos;t claim yet, or invoices you haven&apos;t recorded, show up before you file.
        </p>
        <Row className="g-3 align-items-end">
          <Col md={6}>
            <Form.Group>
              <Form.Label>GSTR-2B JSON file</Form.Label>
              <Form.Control ref={fileInput} type="file" accept=".json,application/json" onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); }} />
            </Form.Group>
          </Col>
          <Col md="auto">
            <Button variant="primary" onClick={reconcile} disabled={!file || busy}>{busy ? 'Comparing...' : 'Reconcile'}</Button>
          </Col>
        </Row>

        {result && (
          <div className="mt-4">
            {result.warnings.map((w) => <Alert key={w} variant="warning" className="py-1 small mb-2">{w}</Alert>)}
            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              <span className="text-muted small me-2">{result.documentsIn2b} documents in 2B for {result.period || 'the file'}</span>
              {Object.keys(STATUS_LABEL).map((k) => (
                <Badge key={k} bg={STATUS_COLOR[k]} text={STATUS_COLOR[k] === 'warning' ? 'dark' : undefined}
                  style={{ cursor: 'pointer', opacity: filter === 'ALL' || filter === k ? 1 : 0.5 }}
                  onClick={() => setFilter(filter === k ? 'ALL' : k)}>
                  {STATUS_LABEL[k]}: {sm[k]}
                </Badge>
              ))}
              <Button size="sm" variant="outline-secondary" className="ms-auto" onClick={downloadCsv}>Download CSV</Button>
            </div>
            <Table bordered size="sm" style={{ maxWidth: 520 }}>
              <thead><tr><th>Input credit</th><th className="text-end">IGST</th><th className="text-end">CGST</th><th className="text-end">SGST</th><th className="text-end">Total</th></tr></thead>
              <tbody>
                <tr><td>In 2B (net of credit notes)</td><td className="text-end">{n2(sm.itcIn2b.igst)}</td><td className="text-end">{n2(sm.itcIn2b.cgst)}</td><td className="text-end">{n2(sm.itcIn2b.sgst)}</td><td className="text-end">{n2(sm.itcIn2b.total)}</td></tr>
                <tr><td>In your books (all recorded, net of return notes)</td><td className="text-end">{n2(sm.itcInBooks.igst)}</td><td className="text-end">{n2(sm.itcInBooks.cgst)}</td><td className="text-end">{n2(sm.itcInBooks.sgst)}</td><td className="text-end">{n2(sm.itcInBooks.total)}</td></tr>
              </tbody>
            </Table>
            <Table bordered size="sm" responsive>
              <thead>
                <tr><th>Result</th><th>Type</th><th>Supplier</th><th>Number</th><th>Date</th>
                  <th className="text-end">2B taxable</th><th className="text-end">2B tax</th><th className="text-end">Books taxable</th><th className="text-end">Books tax</th><th>Detail</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.section}-${r.number}-${i}`}>
                    <td><Badge bg={STATUS_COLOR[r.status]} text={STATUS_COLOR[r.status] === 'warning' ? 'dark' : undefined}>{STATUS_LABEL[r.status]}</Badge></td>
                    <td>{r.section === 'NOTE' ? 'Note' : 'Invoice'}</td>
                    <td>{r.supplierName || '-'}<div className="small text-muted">{r.supplierGstin}</div></td>
                    <td>{r.number}{r.booksRef && <div className="small text-muted">{r.booksRef}</div>}</td>
                    <td>{formatDate(r.date) || '-'}</td>
                    <td className="text-end">{n2(r.taxable2b)}</td>
                    <td className="text-end">{r.taxable2b === undefined ? '-' : n2(Number(r.igst2b) + Number(r.cgst2b) + Number(r.sgst2b))}</td>
                    <td className="text-end">{n2(r.booksTaxable)}</td>
                    <td className="text-end">{r.booksTaxable === undefined ? '-' : n2(Number(r.booksIgst) + Number(r.booksCgst) + Number(r.booksSgst))}</td>
                    <td className="small">{r.message}{r.itcAvailable === 'N' && <div className="text-danger">The supplier marked credit as not available</div>}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan="10" className="text-center text-muted">Nothing to show.</td></tr>}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

/** Prepared GSTR-1 and GSTR-3B workings for a month, with portal JSON and CSV downloads. Nothing is filed from here. */
function GstReturns({ active }) {
  const [period, setPeriod] = useState(lastMonth());
  const [gstr1, setGstr1] = useState(null);
  const [gstr3b, setGstr3b] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active || !period) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get('/api/gst-returns/gstr1', { params: { period } }),
      axios.get('/api/gst-returns/gstr3b', { params: { period } })
    ])
      .then(([a, b]) => { if (!cancelled) { setGstr1(a.data); setGstr3b(b.data); } })
      .catch((error) => {
        if (!cancelled) {
          setGstr1(null); setGstr3b(null);
          toast.error(typeof error.response?.data === 'string' ? error.response.data : 'Could not prepare the returns');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, period]);

  const download = async (path, format, fallback) => {
    try {
      const response = await axios.get(`/api/gst-returns/${path}/export`, { params: { period, format }, responseType: 'blob' });
      saveBlobResponse(response, fallback);
    } catch (error) {
      toast.error(await blobError(error, 'Download failed'));
    }
  };

  const s = gstr1?.summary || {};
  const j3 = gstr3b?.portalJson;
  const sup = j3?.sup_details;
  const itc = j3?.itc_elg;

  return (
    <div className="mt-3">
      <Alert variant="warning" className="py-2 small">
        These are working papers prepared from your books, not filed returns. Have your accountant check them against the GST portal before filing.
      </Alert>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Return month</Form.Label>
            <Form.Control type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </Form.Group>
        </Col>
        {loading && <Col md="auto"><Spinner animation="border" size="sm" /></Col>}
      </Row>

      <FeatureNotice feature="GST_2B_RECONCILIATION" className="mt-3">The reconciliation can’t be run.</FeatureNotice>
      <Gstr2bReconciliation />

      {gstr1 && (
        <Card className="mb-4">
          <Card.Header><h5 className="mb-0">GSTR-1 — outward supplies</h5></Card.Header>
          <Card.Body>
            {gstr1.warnings.map((w) => <Alert key={w} variant="warning" className="py-1 small mb-2">{w}</Alert>)}
            <Row className="mb-3">
              <Col md={3}><div className="text-muted small">Sale bills / notes</div><strong>{s.bills} / {s.notes}</strong></Col>
              <Col md={3}><div className="text-muted small">B2B invoices (buyers)</div><strong>{s.b2bInvoices} ({s.b2bBuyers})</strong></Col>
              <Col md={3}><div className="text-muted small">B2C large / B2C small rows</div><strong>{s.b2clInvoices} / {s.b2csRows}</strong></Col>
              <Col md={3}><div className="text-muted small">Credit-debit notes (registered / unregistered)</div><strong>{s.cdnrNotes} / {s.cdnurNotes}</strong></Col>
            </Row>
            <Table bordered size="sm" style={{ maxWidth: 560 }}>
              <thead><tr><th>Net of notes</th><th className="text-end">Taxable</th><th className="text-end">IGST</th><th className="text-end">CGST</th><th className="text-end">SGST</th></tr></thead>
              <tbody>
                <tr><td>Taxable supplies</td><td className="text-end">{n2(s.taxableValue)}</td><td className="text-end">{n2(s.igst)}</td><td className="text-end">{n2(s.cgst)}</td><td className="text-end">{n2(s.sgst)}</td></tr>
                <tr><td>Nil-rated supplies</td><td className="text-end">{n2(s.nilRatedValue)}</td><td /><td /><td /></tr>
              </tbody>
            </Table>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={() => download('gstr1', 'json', `GSTR1_${period}.json`)}>Download portal JSON</Button>
              <Button variant="outline-secondary" onClick={() => download('gstr1', 'csv', `gstr1-${period}.zip`)}>Download CSVs (zip)</Button>
            </div>
            <Form.Text className="text-muted d-block mt-2">
              The JSON needs your GSTIN in Org Admin &gt; Org settings. The HSN table follows the portal&apos;s B2B / B2C split; check the file with the portal&apos;s offline tool before uploading.
            </Form.Text>
          </Card.Body>
        </Card>
      )}

      {gstr3b && sup && itc && (
        <Card className="mb-4">
          <Card.Header><h5 className="mb-0">GSTR-3B — summary</h5></Card.Header>
          <Card.Body>
            {gstr3b.warnings.map((w) => <Alert key={w} variant="warning" className="py-1 small mb-2">{w}</Alert>)}
            <Table bordered size="sm" responsive>
              <thead><tr><th>Table</th><th>Description</th><th className="text-end">Taxable</th><th className="text-end">IGST</th><th className="text-end">CGST</th><th className="text-end">SGST</th></tr></thead>
              <tbody>
                <tr><td>3.1(a)</td><td>Outward taxable supplies</td><td className="text-end">{n2(sup.osup_det.txval)}</td><td className="text-end">{n2(sup.osup_det.iamt)}</td><td className="text-end">{n2(sup.osup_det.camt)}</td><td className="text-end">{n2(sup.osup_det.samt)}</td></tr>
                <tr><td>3.1(c)</td><td>Nil-rated outward supplies</td><td className="text-end">{n2(sup.osup_nil_exmp.txval)}</td><td /><td /><td /></tr>
                <tr><td>3.1(d)</td><td>Inward supplies on reverse charge</td><td className="text-end">{n2(sup.isup_rev.txval)}</td><td className="text-end">{n2(sup.isup_rev.iamt)}</td><td className="text-end">{n2(sup.isup_rev.camt)}</td><td className="text-end">{n2(sup.isup_rev.samt)}</td></tr>
                {j3.inter_sup.unreg_details.map((r) => (
                  <tr key={r.pos}><td>3.2</td><td>Inter-state to unregistered, place of supply {r.pos}</td><td className="text-end">{n2(r.txval)}</td><td className="text-end">{n2(r.iamt)}</td><td /><td /></tr>
                ))}
                {itc.itc_avl.filter((r) => ['ISRC', 'OTH'].includes(r.ty)).map((r) => (
                  <tr key={`a${r.ty}`}><td>4(A)</td><td>ITC available — {r.ty === 'ISRC' ? 'reverse charge' : 'all other'}</td><td /><td className="text-end">{n2(r.iamt)}</td><td className="text-end">{n2(r.camt)}</td><td className="text-end">{n2(r.samt)}</td></tr>
                ))}
                {itc.itc_rev.filter((r) => r.ty === 'OTH').map((r) => (
                  <tr key="rev"><td>4(B)</td><td>ITC reversed — others</td><td /><td className="text-end">{n2(r.iamt)}</td><td className="text-end">{n2(r.camt)}</td><td className="text-end">{n2(r.samt)}</td></tr>
                ))}
                <tr className="fw-bold"><td>4(C)</td><td>Net ITC available</td><td /><td className="text-end">{n2(itc.itc_net.iamt)}</td><td className="text-end">{n2(itc.itc_net.camt)}</td><td className="text-end">{n2(itc.itc_net.samt)}</td></tr>
                {itc.itc_inelg.filter((r) => r.ty === 'OTH').map((r) => (
                  <tr key="inelg"><td>4(D)</td><td>Ineligible ITC</td><td /><td className="text-end">{n2(r.iamt)}</td><td className="text-end">{n2(r.camt)}</td><td className="text-end">{n2(r.samt)}</td></tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={() => download('gstr3b', 'json', `GSTR3B_${period}.json`)}>Download portal JSON</Button>
              <Button variant="outline-secondary" onClick={() => download('gstr3b', 'csv', `gstr3b-${period}.csv`)}>Download CSV</Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

export default GstReturns;
